import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  EyeSlashIcon,
  TrashIcon,
  FlagIcon,
  // UserIcon,
  // ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import Button from '../../../components/ui/Button';
import Card, { CardContent } from '../../../components/ui/Card';
import { commentsService, type Comment } from '../../../services/commentsService';
// import { creatorService } from '../../../services/creatorService';
import { postsService } from '../../../services/postsService';

interface CommentWithPost extends Comment {
  post: {
    id: string;
    title: string;
  };
}

const CommentModerationPage: React.FC = () => {
  const [comments, setComments] = useState<CommentWithPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'hidden' | 'deleted'>('all');
  const [selectedComments, setSelectedComments] = useState<Set<string>>(new Set());
  // const [posts, setPosts] = useState<any[]>([]);

  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    try {
      setLoading(true);
      // Load all posts from the creator
      const postsRes = await postsService.list({ limit: 100, offset: 0 });
  const creatorPosts = postsRes.posts;

      // Load comments for all posts
      const allComments: CommentWithPost[] = [];
      for (const post of creatorPosts) {
        try {
          const commentsRes = await commentsService.getComments(post.id);
          const commentsWithPost = commentsRes.comments.map((comment) => ({
            ...comment,
            post: {
              id: post.id,
              title: post.title || 'Untitled Post',
            },
          }));
          allComments.push(...commentsWithPost);
        } catch (error) {
          console.error(`Failed to load comments for post ${post.id}:`, error);
        }
      }

      setComments(allComments);
    } catch (error: any) {
      console.error('Failed to load comments:', error);
      alert(error?.response?.data?.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (commentId: string, action: 'APPROVE' | 'DELETE' | 'HIDE' | 'FLAG') => {
    try {
      // Find the comment to get postId
      const comment = comments.find((c) => c.id === commentId);
      if (!comment) return;

      switch (action) {
        case 'APPROVE':
          // Approve comment (typically just unhide if hidden)
          await commentsService.updateComment(comment.postId, commentId, comment.content);
          break;
        case 'DELETE':
          await commentsService.deleteComment(comment.postId, commentId);
          break;
        case 'HIDE':
          // Hide comment (update content to hidden state or use a hide endpoint)
          await commentsService.updateComment(comment.postId, commentId, comment.content);
          break;
        case 'FLAG':
          // Flag comment (report it)
          // This would typically call a report endpoint
          alert('Flag functionality would be implemented here');
          break;
      }
      await loadComments();
      alert(`Comment ${action.toLowerCase()}d successfully`);
    } catch (error: any) {
      console.error(`Failed to ${action.toLowerCase()} comment:`, error);
      alert(error?.response?.data?.message || `Failed to ${action.toLowerCase()} comment`);
    }
  };

  const handleBulkAction = async (action: 'APPROVE' | 'DELETE' | 'HIDE') => {
    if (selectedComments.size === 0) {
      alert('Please select comments to moderate');
      return;
    }

    if (!confirm(`Are you sure you want to ${action.toLowerCase()} ${selectedComments.size} comment(s)?`)) {
      return;
    }

    try {
      for (const commentId of selectedComments) {
        await handleModerate(commentId, action);
      }
      setSelectedComments(new Set());
      await loadComments();
      alert(`Successfully ${action.toLowerCase()}d ${selectedComments.size} comment(s)`);
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
      alert('Failed to perform bulk action');
    }
  };

  const toggleSelect = (commentId: string) => {
    const newSelected = new Set(selectedComments);
    if (newSelected.has(commentId)) {
      newSelected.delete(commentId);
    } else {
      newSelected.add(commentId);
    }
    setSelectedComments(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedComments.size === comments.length) {
      setSelectedComments(new Set());
    } else {
      setSelectedComments(new Set(comments.map((c) => c.id)));
    }
  };

  const getFilteredComments = () => {
    switch (filter) {
      case 'pending':
        // Comments that need moderation (new or flagged)
        return comments; // TODO: Filter by pending status
      case 'approved':
        return comments.filter((c) => !c.isDeleted); // TODO: Check approved status
      case 'hidden':
        return comments.filter((c) => c.isDeleted); // TODO: Check hidden status
      case 'deleted':
        return comments.filter((c) => c.isDeleted);
      default:
        return comments;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Comment Moderation</h1>
          <p className="text-gray-600 mt-1">Review and moderate comments on your posts</p>
        </div>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-patreon-orange text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-patreon-orange text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'approved'
                ? 'bg-patreon-orange text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilter('hidden')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'hidden'
                ? 'bg-patreon-orange text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Hidden
          </button>
        </div>

        {selectedComments.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">{selectedComments.size} selected</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('APPROVE')}
              leftIcon={<CheckCircleIcon className="w-4 h-4" />}
            >
              Approve
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('HIDE')}
              leftIcon={<EyeSlashIcon className="w-4 h-4" />}
            >
              Hide
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction('DELETE')}
              leftIcon={<TrashIcon className="w-4 h-4" />}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Comments List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-400">Loading comments...</div>
        </div>
      ) : getFilteredComments().length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FlagIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No comments found</h3>
            <p className="text-gray-600">Comments awaiting moderation will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Select All */}
          <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
            <input
              type="checkbox"
              checked={selectedComments.size === comments.length && comments.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 text-patreon-orange rounded focus:ring-patreon-orange"
            />
            <label className="text-sm text-gray-700 font-medium">Select All</label>
          </div>

          {getFilteredComments().map((comment) => (
            <Card key={comment.id}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    checked={selectedComments.has(comment.id)}
                    onChange={() => toggleSelect(comment.id)}
                    className="mt-1 w-4 h-4 text-patreon-orange rounded focus:ring-patreon-orange"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {comment.author.avatar ? (
                          <img
                            src={comment.author.avatar}
                            alt={comment.author.username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-patreon-orange rounded-full flex items-center justify-center text-white font-semibold">
                            {comment.author.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <h4 className="font-semibold text-gray-900">{comment.author.username}</h4>
                          <p className="text-sm text-gray-500">
                            {new Date(comment.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {comment.isDeleted && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          Deleted
                        </span>
                      )}
                    </div>

                    <p className="text-gray-700 mb-2">{comment.content}</p>
                    
                    <p className="text-sm text-gray-500 mb-4">
                      On: <span className="font-medium text-gray-700">{comment.post.title}</span>
                    </p>

                    <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleModerate(comment.id, 'APPROVE')}
                        leftIcon={<CheckCircleIcon className="w-4 h-4" />}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleModerate(comment.id, 'HIDE')}
                        leftIcon={<EyeSlashIcon className="w-4 h-4" />}
                      >
                        Hide
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleModerate(comment.id, 'FLAG')}
                        leftIcon={<FlagIcon className="w-4 h-4" />}
                      >
                        Flag
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleModerate(comment.id, 'DELETE')}
                        leftIcon={<TrashIcon className="w-4 h-4" />}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentModerationPage;


